/**
 * The link wrapper the three number tiles share (2026-08-19).
 *
 * ## Why it exists
 *
 * `kpi`, `alert` and `count` each carried the same three-way branch — no target,
 * internal, external — inline in their JSX. Three copies of a SECURITY-relevant
 * shape is three places to get it subtly wrong, and the wrongness is silent: a
 * missing `rel`, or an `else` that falls back to the raw href, looks fine until
 * someone authors a hostile one.
 *
 * ## 🔴 IT DOES NOT SANITISE, AND MUST NOT
 *
 * It takes an ALREADY-RESOLVED target. `sanitizeKpiHref` stays at each call site
 * because that is where the edit-mode gate also lives (`editMode ? null :
 * sanitizeKpiHref( href )`), and folding the sanitiser in here would create a
 * second entry point that a future caller could reach without the gate.
 *
 * What it does guarantee is the FAIL-CLOSED shape: a `null` target renders the
 * children with NO anchor at all — never a dead `<a>`, never `href="#"`, never a
 * raw fallback. See the C5 block in `AlertWidget` for why that matters on a
 * shared, cross-user document.
 *
 * ## The whole tile is the target
 *
 * Ticket owner, 2026-08-19: *"widgets with link, should navigate the whole widget
 * not just the number?"* — so callers wrap their entire body with this, not just
 * the value. A 4×5 card whose only clickable pixel is a number the size of the
 * text is a link you have to aim at; the card already reads as one object.
 */
import { Link } from 'react-router';
import type { ReactNode } from 'react';
import type { KpiHref } from './kpiData';

export type WidgetLinkProps = {
  /**
   * The resolved target, or `null` for "no link" — which includes both "no href
   * was authored" and "the href failed the allow-list". The two are deliberately
   * indistinguishable here: a rejected href must look exactly like an absent one,
   * or the styling difference invites someone to "fix" it by loosening the
   * sanitiser.
   */
  target: KpiHref | null;
  testId: string;
  className?: string;
  children: ReactNode;
};

export const WidgetLink = ({ target, testId, className, children }: WidgetLinkProps ) => {
  if ( target === null ) return <>{children}</>;

  if ( target.kind === 'internal' ) {
    return (
      <Link to={target.href} className={className} data-testid={testId}>
        {children}
      </Link>
    );
  }

  return (
    <a
      href={target.href}
      className={className}
      data-testid={testId}
      // Vetted as http(s) by the sanitiser, but still a hop to a host we do not
      // control — so the new context gets no handle back to this one.
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
};
